import React, { useState, useEffect } from 'react';
import { NoSymbolIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { apiClient } from '@/core/api/client';

interface BannedSender {
  id: string;
  email_address: string;
  reason: string;
  created_at: string;
}

export const BannedSendersPanel: React.FC = () => {
  const [senders, setSenders] = useState<BannedSender[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newReason, setNewReason] = useState('Spam');

  const fetchBannedSenders = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get<{ success: boolean; data: BannedSender[] }>(
        '/api/admin/emails/banned-senders'
      );
      if (response.success) {
        setSenders(response.data);
      }
    } catch (error) {
      console.error('Error fetching banned senders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBannedSenders();
  }, []);

  const handleAdd = async () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed) return;

    try {
      await apiClient.post('/api/admin/emails/ban-sender', {
        email_address: trimmed,
        reason: newReason || 'Spam'
      });
      setNewEmail('');
      setNewReason('Spam');
      fetchBannedSenders();
    } catch (error) {
      console.error('Error banning sender:', error);
    }
  };

  const handleRemove = async (emailAddress: string) => {
    if (!window.confirm(`Débloquer "${emailAddress}" ?`)) return;

    try {
      await apiClient.delete('/api/admin/emails/ban-sender', {
        data: { email_address: emailAddress }
      });
      fetchBannedSenders();
    } catch (error) {
      console.error('Error unbanning sender:', error);
    }
  };

  if (isLoading) {
    return <div className="banned-senders-loading">Chargement...</div>;
  }

  return (
    <div className="banned-senders-panel">
      <div className="banned-senders-header">
        <NoSymbolIcon className="banned-senders-header-icon" />
        <div>
          <h3>Expéditeurs bloqués</h3>
          <p>Les emails de ces expéditeurs sont automatiquement archivés. Utilisez <code>@domaine.com</code> pour bloquer un domaine entier.</p>
        </div>
      </div>

      <div className="banned-senders-add">
        <input
          type="text"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="email@exemple.com ou @domaine.com"
          className="banned-senders-input"
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
        <input
          type="text"
          value={newReason}
          onChange={(e) => setNewReason(e.target.value)}
          placeholder="Raison"
          className="banned-senders-reason"
        />
        <button
          className="banned-senders-add-btn"
          onClick={handleAdd}
          disabled={!newEmail.trim()}
        >
          <PlusIcon className="banned-senders-btn-icon" />
          Bloquer
        </button>
      </div>

      {senders.length === 0 ? (
        <div className="banned-senders-empty">
          Aucun expéditeur bloqué
        </div>
      ) : (
        <div className="banned-senders-list">
          {senders.map((sender) => (
            <div key={sender.id} className="banned-sender-item">
              <div className="banned-sender-info">
                <span className="banned-sender-email">
                  {sender.email_address.startsWith('@') ? (
                    <><span className="banned-sender-domain-tag">Domaine</span> {sender.email_address}</>
                  ) : (
                    sender.email_address
                  )}
                </span>
                <span className="banned-sender-reason">{sender.reason}</span>
              </div>
              <button
                className="banned-sender-remove"
                onClick={() => handleRemove(sender.email_address)}
                title="Débloquer"
              >
                <TrashIcon className="banned-senders-btn-icon" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
